/**
 * Upload Check Design — PATTERN-ONLY import.
 *
 * The user uploads a photo of a check whose DESIGN they like (their
 * own check, or one they have the right to use — attested in the
 * panel). What crosses into the studio is only the background
 * design: a clean pattern swatch the user frames inside the photo,
 * plus the design's paper/accent colours sampled from that swatch.
 * The studio's own fields then print the user's own details on top.
 *
 * What must never cross: names, addresses, routing / account /
 * check numbers, MICR content, signatures, or any write-up —
 * neither as data nor as a placed image region. Two mechanisms
 * hold that line. Structurally, no text is ever read from the
 * photo: the only data extracted is the swatch's colours. And
 * before a framed swatch may be applied at all, it passes the
 * writing gate (analyzeInk): a crop whose ink reads as writing,
 * numbers or linework is REFUSED outright, so a writing-bearing
 * crop is never tiled onto a produced check. The import is a
 * texture, not a copy of the document: the swatch is drawn as the
 * paper design only, it never enters the MICR clear band, and
 * every printed element on the check is rendered from the
 * studio's own state. No clone engine is involved.
 */

export interface CropRegion {
    /** Insets as % of the photo's width/height to trim from each edge. */
    left: number;
    top: number;
    right: number;
    bottom: number;
}

// The default frame is NOT the whole photo and NOT the central
// band. The centre of a check face is exactly where the write-ups
// live: on the reference GreenFi photo (image-30) the old central
// band {10,34,10,34} sits on the written-amount line and catches
// the handwriting, the amount box and the GreenFi wordmark —
// tiling it papered the face with writing fragments (live render
// check 2026-11-04, the failure this default replaces).
//
// The default is the inter-write-up gap a standard check face
// leaves open: BELOW the amount-words line (which ends ~53% down
// the face) and ABOVE the memo/signature line (~63%), starting
// RIGHT of the bank wordmark/logo zone (which occupies the left
// ~40% of that band) and ending LEFT of the check's right border
// strip (which carries vertical microprint on FSC designs and
// the MP mark on others — at a 4% right inset the frame clipped
// that strip and its sliver tiled as legible text columns even
// though its share of the band was too small and pale for the
// gate to see). This strip is where scenic and guilloche design
// bands are actually printed. Measured on image-30 with
// analyzeInk: this band reads ink 0.000 / core 0.000 / edge 0.000
// (clean) against the old central band's 0.080 / 0.026 / 0.081
// (writing, refused by the gate). The user still adjusts the
// frame freely from this starting point, and the writing gate —
// not the default — is the guarantee: a photo whose gap is not
// clean (a second check in frame, a shifted layout) is refused
// at apply, never tiled.
export const DEFAULT_CROP: CropRegion = { left: 40, top: 54, right: 7, bottom: 38 };

// Insets may run to 88% per side, so a large clean area (most of a
// check photo) can be framed, not just a small central window. The
// panel additionally caps opposite pairs at 92% combined so a sane
// minimum frame always remains; cropRect's 8px floor is the last
// line of defence.
const clampInset = (v: number) => Math.max(0, Math.min(88, Math.round(v || 0)));

/** The source rectangle (in image pixels) left after the insets. */
export function cropRect(imgW: number, imgH: number, region: CropRegion): { x: number; y: number; w: number; h: number } {
    const l = (clampInset(region.left) / 100) * imgW;
    const r = (clampInset(region.right) / 100) * imgW;
    const t = (clampInset(region.top) / 100) * imgH;
    const b = (clampInset(region.bottom) / 100) * imgH;
    return {
        x: Math.round(l),
        y: Math.round(t),
        w: Math.max(8, Math.round(imgW - l - r)),
        h: Math.max(8, Math.round(imgH - t - b))
    };
}

const toHex = (r: number, g: number, b: number) =>
    `#${[r, g, b].map(c => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0')).join('')}`;

/** Mean colour of a pixel array — the design's paper colour. */
export function averageColor(data: Uint8ClampedArray): string {
    let r = 0, g = 0, b = 0, n = 0;
    for (let i = 0; i + 3 < data.length; i += 16) {
        r += data[i]; g += data[i + 1]; b += data[i + 2]; n++;
    }
    if (!n) return '#f8f4ec';
    return toHex(r / n, g / n, b / n);
}

interface HueBucket { hueKey: number; count: number; r: number; g: number; b: number }

/** Saturated-hue histogram of a swatch, ranked strongest first. */
function rankedHueBuckets(data: Uint8ClampedArray): HueBucket[] {
    const buckets = new Map<number, HueBucket>();
    for (let i = 0; i + 3 < data.length; i += 16) {
        const r = data[i], g = data[i + 1], b = data[i + 2];
        const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
        if (mx === 0) continue;
        const sat = (mx - mn) / mx;
        const light = mx / 255;
        if (sat < 0.28 || light < 0.12 || light > 0.92) continue;
        let hue: number;
        if (mx === r) hue = ((g - b) / (mx - mn)) % 6;
        else if (mx === g) hue = (b - r) / (mx - mn) + 2;
        else hue = (r - g) / (mx - mn) + 4;
        hue = Math.round(((hue * 60) + 360) % 360 / 15); // 24 hue buckets
        const bucket = buckets.get(hue) || { hueKey: hue, count: 0, r: 0, g: 0, b: 0 };
        bucket.count++; bucket.r += r; bucket.g += g; bucket.b += b;
        buckets.set(hue, bucket);
    }
    // Stable sort: ties keep first-seen order, matching the
    // long-standing dominantAccent pick. (forEach+push, not a
    // spread of the Map iterator — the project's TS target is
    // es5 without downlevelIteration.)
    const ranked: HueBucket[] = [];
    buckets.forEach(bk => ranked.push(bk));
    ranked.sort((a, b) => b.count - a.count);
    return ranked;
}

const bucketHex = (bk: HueBucket) => toHex(bk.r / bk.count, bk.g / bk.count, bk.b / bk.count);

/**
 * The design's accent colour: the dominant SATURATED hue in the
 * swatch (paper is near-neutral, so saturation is what separates
 * ink/design colour from paper). Returns null when the swatch has
 * no meaningful saturated colour (a plain paper design).
 */
export function dominantAccent(data: Uint8ClampedArray): string | null {
    const top = rankedHueBuckets(data)[0];
    if (!top || top.count < 12) return null;
    return bucketHex(top);
}

/**
 * The design's SECOND colour: the strongest saturated hue at
 * least ~45 degrees away from the primary accent (a two-colour
 * design — deep green waves with a gold seal, navy with burgundy).
 * Drives the frame's secondary hairline. Null when the design is
 * effectively one-colour.
 */
export function secondaryAccent(data: Uint8ClampedArray): string | null {
    const ranked = rankedHueBuckets(data);
    if (ranked.length < 2 || ranked[0].count < 12) return null;
    const primaryHue = ranked[0].hueKey;
    for (const bk of ranked.slice(1)) {
        const apart = Math.min(Math.abs(bk.hueKey - primaryHue), 24 - Math.abs(bk.hueKey - primaryHue));
        if (apart >= 3 && bk.count >= 12) return bucketHex(bk);
    }
    return null;
}

const hexToRgb = (hex: string): [number, number, number] => {
    const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
    if (!m) return [0, 0, 0];
    const n = parseInt(m[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** Blend a hex colour toward another hex colour by fraction f. */
export function mixHex(hex: string, target: string, f: number): string {
    const [r, g, b] = hexToRgb(hex);
    const [tr, tg, tb] = hexToRgb(target);
    const t = Math.max(0, Math.min(1, f));
    return toHex(r + (tr - r) * t, g + (tg - g) * t, b + (tb - b) * t);
}

/** A deeper tone of a colour (toward black) — frame-weight ink. */
export const deepTone = (hex: string) => mixHex(hex, '#000000', 0.32);
/** A lighter tone of a colour (toward white) — hairline weight. */
export const lightTone = (hex: string) => mixHex(hex, '#ffffff', 0.38);

/**
 * Fraction of sampled pixels that are near-black ink. A clean
 * design swatch is paper + mid-tone design colour, so its dark-ink
 * share is tiny; a frame that still contains writing, a MICR line
 * or a heavy printed frame carries measurably more. Heuristic
 * only — it drives a "tighten your frame" hint in the panel, it
 * never blocks the import (the attestation is the user's).
 *
 * SUPERSEDED for gating by analyzeInk below: on real scanned
 * photos this near-black test is nearly blind (the GreenFi
 * writing band measures 0.0005 — anti-aliased scan ink rarely
 * drops a channel under 80), which is why the hint it drove never
 * appeared on the live build. Kept for compatibility; the gate
 * uses analyzeInk.
 */
export function inkCoverage(data: Uint8ClampedArray): number {
    let dark = 0, n = 0;
    for (let i = 0; i + 3 < data.length; i += 16) {
        n++;
        if (Math.max(data[i], data[i + 1], data[i + 2]) < 80) dark++;
    }
    return n ? dark / n : 0;
}

/** Above this dark-ink share, a frame probably contains writing. */
export const INK_COVERAGE_WARN = 0.02;

export interface InkAnalysis {
    /** Near-black share (the legacy inkCoverage measure). */
    darkShare: number;
    /** Share of pixels carrying ink: >=55 luminance below the
     *  swatch's own paper, or saturated and >=35 below it. */
    inkShare: number;
    /** Share of pixels at pen/print darkness: >=110 below paper. */
    coreShare: number;
    /** Share of pixels beside a hard luminance step (>=70 to a
     *  neighbour 2px away) — thin-stroke structure. */
    edgeShare: number;
    /** True when any share exceeds the writing gate. */
    blocked: boolean;
}

/**
 * The writing gate's thresholds. A framed crop scoring above ANY
 * of them reads as containing writing / numbers / linework rather
 * than pattern texture, and the apply is refused.
 *
 * Calibrated on the real specimen photos (harness:
 * iso-patgate/measure_bands.py, extraction-identical sampling):
 *  - REFUSED, every one: the GreenFi central band (ink .080 /
 *    core .026 / edge .081), both holder blocks (.074/.030/.066,
 *    .074/.023/.072), both signature zones (.072/.021/.061,
 *    .134/.101/.075), the GreenFi wordmark (.168/.039/.110), the
 *    whole GreenFi photo (.058/.017/.048), the ANB+USAA default
 *    band (.099/.055/.075), and the GreenFi FSC microprint strip
 *    (ink .040 — microprint is writing; only inkShare catches it).
 *  - ACCEPTED with 3-13x margin: clean pattern bands under the
 *    amount-words line (ink .005-.009 / core .000-.003 /
 *    edge .001-.003) and pale real-world security print, whose
 *    lines sit within ~45 luminance of the paper and score ~0.
 * The gate errs toward refusal: a very dark or bold design region
 * (deep scenic print, near-opaque guilloche) can also trip it.
 * That cost is accepted — the boundary it protects (a crop
 * containing writing is never tiled onto a produced check) is
 * the product's cloning boundary, and pale pattern areas, the
 * common real-check case, pass untouched.
 */
export const WRITING_GATE = { inkShare: 0.03, coreShare: 0.012, edgeShare: 0.04 };

const lumOf = (data: Uint8ClampedArray, i: number) =>
    0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];

/**
 * Measure a swatch's ink structure on a stride-2 pixel grid (the
 * same sampling the calibration harness used): how much of the
 * crop is ink, how much of it is at pen/print darkness, and how
 * much sits beside a hard luminance step. Writing is thin, dark
 * and high-contrast, so its strokes score on all three; pattern
 * texture (pale guilloche, scenic washes, plain paper) is smooth
 * and near the paper tone, so it scores near zero.
 */
export function analyzeInk(data: Uint8ClampedArray, width: number, height: number): InkAnalysis {
    const empty: InkAnalysis = { darkShare: 0, inkShare: 0, coreShare: 0, edgeShare: 0, blocked: false };
    if (!width || !height || data.length < (width * height * 4)) return empty;
    const xs: number[] = [];
    const ys: number[] = [];
    for (let y = 0; y < height; y += 2) ys.push(y);
    for (let x = 0; x < width; x += 2) xs.push(x);
    const n = xs.length * ys.length;
    if (!n) return empty;
    // Paper estimate: 90th-percentile luminance of the grid.
    const lums: number[] = [];
    for (const y of ys) for (const x of xs) lums.push(lumOf(data, (y * width + x) * 4));
    const sorted = lums.slice().sort((a, b) => a - b);
    const paper = sorted[Math.min(sorted.length - 1, Math.floor(0.9 * (sorted.length - 1)))];
    let dark = 0, ink = 0, core = 0, edge = 0;
    let li = 0;
    for (const y of ys) {
        for (const x of xs) {
            const i = (y * width + x) * 4;
            const lum = lums[li++];
            if (Math.max(data[i], data[i + 1], data[i + 2]) < 80) dark++;
            const drop = paper - lum;
            const mx = Math.max(data[i], data[i + 1], data[i + 2]);
            const mn = Math.min(data[i], data[i + 1], data[i + 2]);
            const sat = mx ? (mx - mn) / mx : 0;
            if (drop >= 55 || (sat >= 0.25 && drop >= 35)) ink++;
            if (drop >= 110) core++;
            let hit = false;
            if (x + 2 < width && Math.abs(lumOf(data, (y * width + x + 2) * 4) - lum) >= 70) hit = true;
            if (!hit && y + 2 < height && Math.abs(lumOf(data, ((y + 2) * width + x) * 4) - lum) >= 70) hit = true;
            if (hit) edge++;
        }
    }
    const inkShare = ink / n, coreShare = core / n, edgeShare = edge / n;
    return {
        darkShare: dark / n,
        inkShare,
        coreShare,
        edgeShare,
        blocked: inkShare > WRITING_GATE.inkShare || coreShare > WRITING_GATE.coreShare || edgeShare > WRITING_GATE.edgeShare
    };
}

export interface ExtractedPattern {
    patternUrl: string;
    paperColor: string;
    accentColor: string | null;
    secondaryColor: string | null;
    inkCoverage: number;
    /** Writing-gate measurement of the framed crop. */
    writing: InkAnalysis;
}

/**
 * Display tile width (in check-canvas units) for the applied
 * pattern. The tile is one quarter of the 850-unit business check
 * width, so a framed motif repeats four times across the face and
 * twice down it — it reads as a repeating paper texture. (At the
 * earlier half-width tile, a swatch whose motif fills the frame —
 * e.g. a watermark word — rendered as one giant word spanning the
 * face, indistinguishable from a stretched single copy.)
 */
export const PATTERN_TILE_WIDTH = 212;

/**
 * Alpha at which the pattern tiles are drawn over the check's
 * paper. The paper under the tiles is the design's OWN sampled
 * paper colour, and it must genuinely participate: at the earlier
 * 0.94 the face was 94% photo pixels, so applying the sampled
 * paper changed almost nothing visible — the live render check
 * (2026-11-04) found the base paper still reading white under a
 * GreenFi import whose green came only from the overlay. At 0.86
 * the pattern stays the dominant texture while the sampled paper
 * tints the whole face the way the design's real paper does.
 */
export const PATTERN_TILE_ALPHA = 0.86;

/**
 * Natural display width for a swatch, from its own aspect. A
 * motif swatch (near-square) tiles at the base quarter-face width.
 * A wide swatch is a framed design BAND — drawn at motif scale it
 * shrank into busy wallpaper and its directional design (waves,
 * scenic bands) read as noise; wider tiles keep the design at a
 * scale closer to the one it was printed at: a third of the face
 * for a band, half the face for a near-full-face design strip.
 */
export function patternTileWidthFor(imgW: number, imgH: number): number {
    if (imgW <= 0 || imgH <= 0) return PATTERN_TILE_WIDTH;
    const aspect = imgW / imgH;
    if (aspect >= 2.1) return 425;
    if (aspect >= 1.55) return 283;
    return PATTERN_TILE_WIDTH;
}

/**
 * Tile size for a swatch of imgW x imgH: the natural display width
 * for its aspect (patternTileWidthFor unless overridden) with the
 * swatch's own aspect preserved — never stretched. A degenerate
 * image (no width) falls back to a square tile.
 */
export function patternTileSize(imgW: number, imgH: number, tileW: number = patternTileWidthFor(imgW, imgH)): { tileW: number; tileH: number } {
    const w = Math.max(1, Math.round(tileW));
    const h = imgW > 0 && imgH > 0 ? Math.max(1, Math.round(w * (imgH / imgW))) : w;
    return { tileW: w, tileH: h };
}

/**
 * Tile origins covering a faceW x clipH area with tileW x tileH
 * tiles, starting at (0,0) and stepping while the origin is inside
 * the area (edge tiles are cut by the caller's clip). Guaranteed to
 * include (0,0) and to leave no gap: consecutive origins differ by
 * exactly one tile size.
 */
export function patternTileOrigins(faceW: number, clipH: number, tileW: number, tileH: number): { x: number; y: number }[] {
    const origins: { x: number; y: number }[] = [];
    if (faceW <= 0 || clipH <= 0 || tileW <= 0 || tileH <= 0) return origins;
    for (let y = 0; y < clipH; y += tileH) {
        for (let x = 0; x < faceW; x += tileW) {
            origins.push({ x, y });
        }
    }
    return origins;
}

export interface PatternTilePlacement { x: number; y: number; flipX: boolean; flipY: boolean }

/**
 * Tile placements with MIRROR parity: every second column is
 * flipped horizontally and every second row vertically. A photo
 * crop is not a seamless tile — plain repetition cuts every wave
 * and gradient at the tile edge, and the eye reads the grid of
 * hard seams instead of the design (the framed-swatch render
 * showed visible banding wherever the swatch's gradient
 * restarted). Mirrored neighbours share the exact edge pixels, so
 * the design flows continuously across the face with no seam and
 * no grid; coverage is identical to patternTileOrigins.
 */
export function patternTilePlacements(faceW: number, clipH: number, tileW: number, tileH: number): PatternTilePlacement[] {
    const placements: PatternTilePlacement[] = [];
    if (faceW <= 0 || clipH <= 0 || tileW <= 0 || tileH <= 0) return placements;
    let row = 0;
    for (let y = 0; y < clipH; y += tileH, row++) {
        let col = 0;
        for (let x = 0; x < faceW; x += tileW, col++) {
            placements.push({ x, y, flipX: col % 2 === 1, flipY: row % 2 === 1 });
        }
    }
    return placements;
}

function loadImageElement(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('image load failed'));
        el.src = dataUrl;
    });
}

/**
 * Extract the pattern swatch from a check photo: crop to the framed
 * region, downscale to a texture-sized canvas, sample its colours.
 * Returns null when the photo cannot be read at all (the caller
 * reports that honestly instead of pretending an extraction ran).
 */
export async function extractDesignPattern(
    dataUrl: string,
    region: CropRegion,
    maxDimension = 1200
): Promise<ExtractedPattern | null> {
    try {
        const img = await loadImageElement(dataUrl);
        const iw = img.naturalWidth || img.width;
        const ih = img.naturalHeight || img.height;
        if (!iw || !ih) return null;
        const rect = cropRect(iw, ih, region);
        const scale = Math.min(1, maxDimension / Math.max(rect.w, rect.h));
        const outW = Math.max(8, Math.round(rect.w * scale));
        const outH = Math.max(8, Math.round(rect.h * scale));
        const canvas = document.createElement('canvas');
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(img, rect.x, rect.y, rect.w, rect.h, 0, 0, outW, outH);
        const pixels = ctx.getImageData(0, 0, outW, outH).data;
        return {
            // JPEG, not PNG: the swatch is a photographic texture,
            // and at the raised 1200px working size a PNG data URL
            // routinely exceeded the account-design save cap, so
            // the imported design silently failed to persist. A
            // quality-0.9 JPEG keeps the design faithful at a
            // fraction of the bytes.
            patternUrl: canvas.toDataURL('image/jpeg', 0.9),
            paperColor: averageColor(pixels),
            accentColor: dominantAccent(pixels),
            secondaryColor: secondaryAccent(pixels),
            inkCoverage: inkCoverage(pixels),
            writing: analyzeInk(pixels, outW, outH)
        };
    } catch {
        return null;
    }
}
